import { useMemo } from 'react';
import { PieChart, Pie, Cell, ResponsiveContainer, BarChart, Bar, XAxis, YAxis, Tooltip, Legend, ReferenceLine } from 'recharts';
import { CheckCircle, AlertTriangle, ArrowUp, ArrowDown } from 'lucide-react';

const COLORS = [
  '#38bdf8', // sky
  '#10b981', // emerald
  '#a78bfa', // violet
  '#f59e0b', // amber
  '#ec4899', // pink
  '#06b6d4', // cyan
  '#f97316', // orange
  '#8b5cf6', // purple
  '#14b8a6', // teal
];

function formatCurrency(value) {
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  }).format(value);
}

function formatPercent(value) {
  return new Intl.NumberFormat('en-US', {
    style: 'percent',
    minimumFractionDigits: 1,
    maximumFractionDigits: 1,
  }).format(value);
}

function CustomTooltip({ active, payload }) {
  if (!active || !payload || !payload.length) return null;
  
  const data = payload[0].payload;
  return (
    <div className="bg-slate-800 border border-slate-600 rounded-lg p-3 shadow-xl">
      <p className="font-medium text-slate-200">{data.category}</p>
      <p className="text-sm text-slate-400 mt-1">
        Current: <span className="text-slate-200 font-mono">{formatPercent(data.current_weight)}</span>
      </p>
      <p className="text-sm text-slate-400">
        Target: <span className="text-slate-200 font-mono">{formatPercent(data.target_weight)}</span>
      </p>
      <p className="text-sm text-slate-400">
        Value: <span className="text-slate-200 font-mono">{formatCurrency(data.current_value)}</span>
      </p>
    </div>
  );
}

function AllocationBar({ allocation, totalValue }) {
  const chartData = useMemo(() => {
    return allocation
      .filter(a => a.current_value > 0 || a.target_weight > 0)
      .map(a => ({
        ...a,
        current: a.current_weight * 100,
        target: a.target_weight * 100,
        variance_dollars: (a.current_weight - a.target_weight) * totalValue,
      }))
      .sort((a, b) => b.current_value - a.current_value);
  }, [allocation, totalValue]);

  return (
    <div className="card">
      <h3 className="card-header">Current vs Target Allocation</h3>
      <div className="h-80">
        <ResponsiveContainer width="100%" height="100%">
          <BarChart
            data={chartData}
            layout="vertical"
            margin={{ top: 5, right: 30, left: 80, bottom: 5 }}
          >
            <XAxis 
              type="number" 
              tickFormatter={(v) => `${v}%`}
              domain={[0, 'dataMax + 5']}
              tick={{ fill: '#94a3b8', fontSize: 12 }}
            />
            <YAxis 
              type="category" 
              dataKey="category" 
              tick={{ fill: '#94a3b8', fontSize: 12 }}
              width={75}
            />
            <Tooltip content={<CustomTooltip />} />
            <Legend 
              wrapperStyle={{ paddingTop: 10 }}
              formatter={(value) => <span className="text-slate-300">{value}</span>}
            />
            <Bar dataKey="current" name="Current" fill="#38bdf8" radius={[0, 4, 4, 0]} />
            <Bar dataKey="target" name="Target" fill="#475569" radius={[0, 4, 4, 0]} />
          </BarChart>
        </ResponsiveContainer>
      </div>
    </div>
  );
}

function AllocationPie({ allocation }) {
  const pieData = useMemo(() => {
    return allocation
      .filter(a => a.current_value > 0)
      .sort((a, b) => b.current_value - a.current_value);
  }, [allocation]);

  return (
    <div className="card">
      <h3 className="card-header">Portfolio Composition</h3>
      <div className="h-80">
        <ResponsiveContainer width="100%" height="100%">
          <PieChart>
            <Pie
              data={pieData}
              dataKey="current_value"
              nameKey="category"
              cx="50%"
              cy="50%"
              innerRadius={60}
              outerRadius={100}
              paddingAngle={2}
              label={({ category, current_weight }) => `${category} ${formatPercent(current_weight)}`}
              labelLine={{ stroke: '#64748b' }}
            >
              {pieData.map((entry, index) => (
                <Cell 
                  key={entry.category} 
                  fill={COLORS[index % COLORS.length]}
                  stroke="transparent"
                />
              ))}
            </Pie>
            <Tooltip content={<CustomTooltip />} />
          </PieChart>
        </ResponsiveContainer>
      </div>
    </div>
  );
}

function AllocationTable({ allocation, totalValue }) {
  const sortedAllocation = useMemo(() => {
    return [...allocation].sort((a, b) => b.current_value - a.current_value);
  }, [allocation]);

  return (
    <div className="card p-0 overflow-hidden">
      <div className="p-4 border-b border-slate-700">
        <h3 className="font-semibold text-slate-100">Allocation Details</h3>
      </div>
      <div className="table-container">
        <table>
          <thead>
            <tr>
              <th>Category</th>
              <th className="text-right">Current</th>
              <th className="text-right">Target</th>
              <th className="text-right">Variance</th>
              <th className="text-right">$ Variance</th>
              <th className="text-center">Status</th>
            </tr>
          </thead>
          <tbody>
            {sortedAllocation.map((cat, index) => {
              const varianceDollars = (cat.current_weight - cat.target_weight) * totalValue;
              return (
                <tr key={cat.category}>
                  <td>
                    <div className="flex items-center gap-2">
                      <div 
                        className="w-3 h-3 rounded-full"
                        style={{ backgroundColor: COLORS[index % COLORS.length] }}
                      />
                      <span className="font-medium text-slate-200">{cat.category}</span>
                    </div>
                  </td>
                  <td className="text-right font-mono">{formatPercent(cat.current_weight)}</td>
                  <td className="text-right font-mono text-slate-400">{formatPercent(cat.target_weight)}</td>
                  <td className={`text-right font-mono ${cat.variance >= 0 ? 'value-positive' : 'value-negative'}`}>
                    <div className="flex items-center justify-end gap-1">
                      {cat.variance > 0 ? <ArrowUp className="w-3 h-3" /> : cat.variance < 0 ? <ArrowDown className="w-3 h-3" /> : null}
                      {formatPercent(Math.abs(cat.variance))}
                    </div>
                  </td>
                  <td className={`text-right font-mono ${varianceDollars >= 0 ? 'value-positive' : 'value-negative'}`}>
                    {formatCurrency(varianceDollars)}
                  </td>
                  <td className="text-center">
                    {cat.status === 'OK' ? (
                      <span className="badge badge-success">
                        <CheckCircle className="w-3 h-3 mr-1" /> OK
                      </span>
                    ) : cat.status === 'OVERWEIGHT' ? (
                      <span className="badge badge-warning">
                        <AlertTriangle className="w-3 h-3 mr-1" /> Over
                      </span>
                    ) : (
                      <span className="badge badge-info">
                        <ArrowDown className="w-3 h-3 mr-1" /> Under
                      </span>
                    )}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </div>
  );
}

export default function AllocationChart({ allocation, totalValue }) {
  if (!allocation || allocation.length === 0) {
    return (
      <div className="card">
        <p className="text-slate-400">No allocation data available</p>
      </div>
    );
  }

  return (
    <div className="space-y-6 animate-fade-in">
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <AllocationPie allocation={allocation} />
        <AllocationBar allocation={allocation} totalValue={totalValue} />
      </div>
      <AllocationTable allocation={allocation} totalValue={totalValue} />
    </div>
  );
}

